import React, { useState, useEffect } from "react";

import Modal, { ModalSizes } from "../common/Modal";
import intl from "components/i18n/ReactIntlWrapper";
import { Box, Button, CircularProgress, TextField, Typography, Stack } from "@mui/material";
import SaveIcon from "@mui/icons-material/Save";

export type WorkflowCommentsOnSubmitProps = {
  input: string;
};
export type WorkflowCommentsModalProps = {
  isOpen: boolean;
  onCloseModal: () => void;
  onSubmit: (input: string) => Promise<void> | void;
  required?: boolean;
};

const WorkflowCommentsModal: React.FC<WorkflowCommentsModalProps> = ({ isOpen, onCloseModal, onSubmit, required = false }) => {
  const [input, setInput] = useState<string>("");
  const [error, setError] = useState<string>("");
  const [submitting, setSubmitting] = useState<boolean>(false);

  // Reset input and error when modal closes or opens
  useEffect(() => {
    if (isOpen) {
      setInput("");
      setError("");
      setSubmitting(false);
    }
  }, [isOpen]);

  const handleSave = async () => {
    // Validate if comment is required
    if (required && !input.trim()) {
      setError(intl.get("workflow.comments.required"));
      return;
    }

    if (onSubmit) {
      setSubmitting(true);
      try {
        await onSubmit(input);
      } finally {
        setSubmitting(false);
      }
    }
  };

  const handleInputChange = (e) => {
    setInput(e.target.value);
    // Clear error when user starts typing
    if (error) {
      setError("");
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      closeModal={onCloseModal}
      size={ModalSizes.small}
      height="auto"
    >
      <Box p={3}>
        <Typography variant="h5" mb={2} textAlign="start">
          {intl.get("workflow.comments.title")}
        </Typography>

        <TextField
          fullWidth
          id="description"
          name="description"
          label={intl.get("workflow.comments.description")}
          placeholder={intl.get("workflow.comments.placeholder")}
          multiline
          rows={5}
          value={input}
          onChange={handleInputChange}
          margin="normal"
          error={!!error}
          helperText={error}
          required={required}
        />

        <Stack direction="row" spacing={2} justifyContent="flex-end" mt={3}>
          <Button variant="outlined" color="inherit" onClick={onCloseModal} disabled={submitting}>
            {intl.get("cancel.button")}
          </Button>
          <Button variant="contained" color="primary" onClick={handleSave} disabled={submitting}>
            {submitting ? <CircularProgress size={18} color="inherit" sx={{ mr: 1 }} /> : <SaveIcon className="mb-1" />}
            {intl.get("save.button")}
          </Button>
        </Stack>
      </Box>
    </Modal>
  );
};

export default WorkflowCommentsModal;
